<template lang="pug">
  management-page(title="Summary"
                :creatable="false"
                :searchable="false"
                :showHeader="false")
    template(v-slot:body)
      .chart-configuration
        .time-range-filter
          aiq-date-picker(v-model="dateRange"
                          type="daterange"
                          placeholder="Select date range",
                          popper-class="metrics-time-range__date-picker-popup"
                          :shortcuts="datePickerOptions.shortcuts"
                          :disabled-date="datePickerOptions.disabledDate"
                          :teleported="false"
                          format="YYYY/MM/DD"
                          :clearable="false"
                          :default-time="getDatePickerDefaultTime()")
      .chart-grid
        aiq-row(:gutter="16")
          aiq-col(:span="12")
            chart(:dateRange="dateRangeUtc"
                            :chartConfig="suggestedResponsesChartConfig"
                            yLabel="Count"
                            :shouldHandlePartialDates="true"
                            @onChartDataChange="onChartDataChange($event,'suggestionsChartData')"
                            @onChartBreakdownChange="suggestionsBucketOption=$event"
                            @onXLabelsChange="xLabelsSuggestions=$event"
                            :formatXTick="xLabelsSuggestionsFormat"
                            :reportFileName="returnReportFileName('Suggestions')"
                            )
          aiq-col(:span="12")
            chart(:dateRange="dateRangeUtc"
                            :chartConfig="dialogsChartConfig"
                            yLabel="Count"
                            :shouldHandlePartialDates="true"
                            :formatXTick="xLabelsDialogsFormat"
                            @onXLabelsChange="xLabelsDialogs=$event"
                            @onChartDataChange="onChartDataChange($event,'dialogsChartData')"
                            @onChartBreakdownChange="dialogsBucketOption=$event"
                            :reportFileName="returnReportFileName('Dialogs')"
                            )
          aiq-col(:span="12")
            chart(:dateRange="dateRangeUtc"
                            :chartConfig="workflowsChartConfig"
                            yLabel="Count"
                            :shouldHandlePartialDates="true"
                            :formatXTick="xLabelsWorkflowFormat"
                            @onXLabelsChange="xLabelsWorkflow=$event"
                            @onChartDataChange="onChartDataChange($event,'workflowsChartData')"
                            @onChartBreakdownChange="workflowsBucketOption=$event"
                            :reportFileName="returnReportFileName('Workflows')"
                            )
          aiq-col(:span="12")
            chart(:dateRange="dateRangeUtc"
                            :chartConfig="assetsChartConfig"
                            yLabel="Count"
                            :shouldHandlePartialDates="true"
                            :formatXTick="xLabelsAssetsFormat"
                            @onXLabelsChange="xLabelsAssets=$event"
                            @onChartDataChange="onChartDataChange($event,'assetsChartData')"
                            @onChartBreakdownChange="assetsBucketOption=$event"
                            :reportFileName="returnReportFileName('Assets')"
                            )
          aiq-col(:span="12")
            chart(:dateRange="dateRangeUtc"
                            :chartConfig="documentsChartConfig"
                            yLabel="Count"
                            :shouldHandlePartialDates="true"
                            :formatXTick="xLabelsDocumentsFormat"
                            @onXLabelsChange="xLabelsDocuments=$event"
                            @onChartDataChange="onChartDataChange($event,'documentsChartData')"
                            @onChartBreakdownChange="documentsBucketOption=$event"
                            :reportFileName="returnReportFileName('Documents')"
                            )
</template>

<script>
import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import Chart from '@/components/Chart.vue';
import { dstDateRangeUtc, datePickerDefaultOptions, defaultDateRange, lastAvailableMonthAndQuarterRange, createPickerDateRangeOption } from '../../configs/dateRange';
import { assetsChartConfig, dialogsChartConfig, documentsChartConfig, suggestedResponsesChartConfig, workflowsChartConfig } from './constants';
import dayjs from '@/localPlugins/dayjs';

export default {
  pageResource: '/metrics',
  components: {
    Chart,
    ManagementPage,
  },
  computed: {
    datePickerOptions() {
      return {
        ...datePickerDefaultOptions(),
        shortcuts: lastAvailableMonthAndQuarterRange.map(createPickerDateRangeOption),
      };
    },
    dateRangeUtc() {
      return dstDateRangeUtc(this.dateRange);
    },
  },
  data() {
    return {
      assetsChartConfig,
      documentsChartConfig,
      suggestedResponsesChartConfig,
      dialogsChartConfig,
      workflowsChartConfig,
      percentFormatter: v => `${v}%`,
      durationFormatter: this.$aiq.formatters.timeDelta,
      dateRange: defaultDateRange(),
      xLabelsSuggestions: {},
      xLabelsDialogs: {},
      xLabelsWorkflow: {},
      xLabelsAssets: {},
      xLabelsDocuments: {},
    };
  },
  methods: {
    xLabelsSuggestionsFormat(date) {
      return this.xAxisFormatForCharts(date, this.xLabelsSuggestions, 'suggestionsChartData');
    },
    xLabelsDialogsFormat(date) {
      return this.xAxisFormatForCharts(date, this.xLabelsDialogs, 'dialogsChartData');
    },
    xLabelsWorkflowFormat(date) {
      return this.xAxisFormatForCharts(date, this.xLabelsWorkflow, 'workflowsChartData');
    },
    xLabelsAssetsFormat(date) {
      return this.xAxisFormatForCharts(date, this.xLabelsAssets, 'assetsChartData');
    },
    xLabelsDocumentsFormat(date) {
      return this.xAxisFormatForCharts(date, this.xLabelsDocuments, 'documentsChartData');
    },
    xAxisFormatForCharts(date, xLabels, chartName) {
      const formattedDate = xLabels[dayjs(date).valueOf()];
      return `${formattedDate} ${this.isDataPartial(date, chartName) ? ' *' : ''}`;
    },
    returnReportFileName(chartName = 'data') {
      const [startTimestamp, endTimestamp] = this.dateRange;
      const startDate = dayjs.isDayjs(startTimestamp)
        ? startTimestamp.format('YYYY-MM-DD')
        : dayjs(startTimestamp).format('YYYY-MM-DD');
      const endDate = dayjs.isDayjs(endTimestamp)
        ? endTimestamp.format('YYYY-MM-DD')
        : dayjs(endTimestamp).format('YYYY-MM-DD');
      return `${chartName.replaceAll(' ', '_')}_${startDate}_${endDate}`;
    },
    onChartDataChange(data, chartName) {
      const dataPoints = data?.dataset?.[0]?.data_points || [];
      this[chartName] = dataPoints;
    },
    isDataPartial(date, chartName) {
      const timeStamp = new Date(date).getTime();
      const isPartial = this?.[chartName]?.find(item => item.x === timeStamp)?.isPartial;
      return isPartial;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../styles/aiq-variables.scss";

.content-inner.summary {
  overflow-y: auto;
}

.time-range-filter {
  position: relative;
  margin-left: auto;
}

.el-card:not(:first-of-type) {
  margin-top: 24px;
}

.chart-configuration {
  display: flex;
  margin-bottom: 24px;

  .members-filter {
    display: flex;
    align-items: center;
  }

  .breakdown {
    display: flex;
    align-items: center;
    flex-grow: 1;
  }

  .export-command {
    display: flex;
    height: 30px;
    width: 40px;
    justify-content: center;
    align-items: center;
    margin-left: 16px;
    padding: 0;
  }
}
</style>
